/**
 * useCelebration.ts
 *
 * Guards:
 *   - Daily gate (localStorage): never evaluates twice in the same local day
 *   - Once-per-launch guard (module-level Set): never re-evaluates for the
 *     same userId within one app session
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  evaluateCelebration,
  hasShownCelebrationToday,
  type CelebrationResult,
} from '../services/celebration.service';

const _evaluatedThisSession = new Set<string>();

interface UseCelebrationResult {
  celebration: CelebrationResult | null;
  dismiss: () => void;
}

export function useCelebration(
  userId: string | undefined,
  dataReady: boolean,
  sourceScreen = 'home',
): UseCelebrationResult {
  const [celebration, setCelebration] = useState<CelebrationResult | null>(null);
  const evaluatingRef = useRef(false);

  useEffect(() => {
    if (!userId || !dataReady) return;
    if (hasShownCelebrationToday()) return;
    if (_evaluatedThisSession.has(userId)) return;
    if (evaluatingRef.current) return;

    evaluatingRef.current = true;
    _evaluatedThisSession.add(userId);

    let cancelled = false;

    async function evaluate() {
      try {
        const result = await evaluateCelebration(userId!, sourceScreen);
        if (!cancelled) setCelebration(result);
      } catch (err) {
        console.error('[useCelebration] Evaluation failed:', err);
        if (!cancelled) setCelebration(null);
      } finally {
        evaluatingRef.current = false;
      }
    }

    evaluate();
    return () => { cancelled = true; };
  }, [userId, dataReady, sourceScreen]);

  const dismiss = useCallback(() => setCelebration(null), []);

  return { celebration, dismiss };
}
